import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  FaGlobe,
  FaPlus,
  FaTrash,
  FaSave,
  FaGripVertical,
  FaEye,
  FaEyeSlash,
} from 'react-icons/fa';
import { useToast } from '../../context/ToastContext';
import PageLayout from '../../components/ui/PageLayout';
import PageHeader from '../../components/ui/PageHeader';
import SkeletonLoader from '../../components/ui/SkeletonLoader';

const emptySection = (index) => ({
  title: '',
  content: '',
  order: index,
});

const LegalPagesCMS = () => {
  const { success: toastSuccess, error: toastError } = useToast();
  const [selectedSlug, setSelectedSlug] = useState('privacy-policy');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: '',
    lastUpdated: '',
    introText: '',
    isPublished: true,
    sections: [],
  });

  const fetchContent = async (slug) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/cms/legal/admin/${slug}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        const data = res.data.data;
        setForm({
          title: data.title || '',
          lastUpdated: data.lastUpdated || '',
          introText: data.introText || '',
          isPublished: data.isPublished !== false,
          sections: (data.sections || []).map((sec, index) => ({
            _id: sec._id,
            title: sec.title || '',
            content: sec.content || '',
            order: typeof sec.order === 'number' ? sec.order : index,
          })),
        });
      }
    } catch (err) {
      toastError(`Failed to load ${slug === 'privacy-policy' ? 'Privacy Policy' : 'Subscription Agreement'} content`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent(selectedSlug);
  }, [selectedSlug]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateSection = (index, field, value) => {
    setForm((prev) => {
      const sections = [...prev.sections];
      sections[index] = { ...sections[index], [field]: value };
      return { ...prev, sections };
    });
  };

  const addSection = () => {
    setForm((prev) => ({
      ...prev,
      sections: [...prev.sections, emptySection(prev.sections.length)],
    }));
  };

  const removeSection = (index) => {
    if (!window.confirm('Remove this section?')) return;
    setForm((prev) => ({
      ...prev,
      sections: prev.sections
        .filter((_, i) => i !== index)
        .map((sec, i) => ({ ...sec, order: i })),
    }));
  };

  const moveSection = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= form.sections.length) return;
    setForm((prev) => {
      const sections = [...prev.sections];
      [sections[index], sections[target]] = [sections[target], sections[index]];
      return {
        ...prev,
        sections: sections.map((sec, i) => ({ ...sec, order: i })),
      };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/cms/legal/${selectedSlug}`,
        form,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        toastSuccess('Legal page content saved successfully');
        fetchContent(selectedSlug);
      }
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageLayout>
      <PageHeader
        title="Legal Pages CMS"
        description="Manage the content for Privacy Policy and Subscription Agreement"
        icon={FaGlobe}
        actions={
          <div className="flex items-center gap-3">
            <select
              value={selectedSlug}
              onChange={(e) => setSelectedSlug(e.target.value)}
              className="px-4 py-2 rounded-xl border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 text-sm font-semibold shadow-sm focus:outline-none"
            >
              <option value="privacy-policy">Privacy Policy</option>
              <option value="subscription-agreement">Subscription Agreement</option>
            </select>
            <button
              onClick={handleSave}
              disabled={saving || loading}
              className="btn-premium flex items-center gap-2"
            >
              <FaSave className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        }
      />

      {loading ? (
        <SkeletonLoader variant="page" />
      ) : (
        <div className="space-y-6">
          {/* Header Info */}
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-6">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-dark-700 pb-4">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Page Settings & Header</h2>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.isPublished}
                  onChange={(e) => updateField('isPublished', e.target.checked)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                Published
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Page Title</label>
                <input
                  className="input-modern"
                  value={form.title}
                  onChange={(e) => updateField('title', e.target.value)}
                  placeholder="e.g. Privacy Policy"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Last Updated text</label>
                <input
                  className="input-modern"
                  value={form.lastUpdated}
                  onChange={(e) => updateField('lastUpdated', e.target.value)}
                  placeholder="e.g. Last updated: 9th Jan 2025"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Intro / Welcome Text (HTML supported)</label>
              <textarea
                className="input-modern min-h-[120px]"
                value={form.introText}
                onChange={(e) => updateField('introText', e.target.value)}
                placeholder="Introductory paragraph(s) at the top of the page"
              />
            </div>
          </div>

          {/* Sections list */}
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-5">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-dark-700 pb-4">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Document Sections</h2>
              <button
                onClick={addSection}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-xl transition-colors"
              >
                <FaPlus className="w-3.5 h-3.5" /> Add Section
              </button>
            </div>

            {form.sections.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-8">
                No sections yet. Click &quot;Add Section&quot; to create one.
              </p>
            ) : (
              <div className="space-y-5">
                {form.sections.map((sec, index) => (
                  <div
                    key={sec._id || `sec-${index}`}
                    className="rounded-2xl border border-gray-200 dark:border-dark-600 bg-gray-50/50 dark:bg-dark-900/30 p-5 space-y-4"
                  >
                    <div className="flex items-center justify-between gap-3 border-b border-gray-100 dark:border-dark-700/50 pb-2">
                      <div className="flex items-center gap-2">
                        <FaGripVertical className="text-gray-400" />
                        <span className="text-sm font-bold text-gray-700 dark:text-gray-200">
                          Section {index + 1}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => moveSection(index, -1)}
                          disabled={index === 0}
                          className="px-2 py-1 text-xs rounded-lg border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 disabled:opacity-40"
                        >
                          Up
                        </button>
                        <button
                          type="button"
                          onClick={() => moveSection(index, 1)}
                          disabled={index === form.sections.length - 1}
                          className="px-2 py-1 text-xs rounded-lg border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 disabled:opacity-40"
                        >
                          Down
                        </button>
                        <button
                          type="button"
                          onClick={() => removeSection(index)}
                          className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                          title="Delete Section"
                        >
                          <FaTrash className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Section Title</label>
                      <input
                        className="input-modern"
                        value={sec.title}
                        onChange={(e) => updateSection(index, 'title', e.target.value)}
                        placeholder="e.g. 1. Definitions / I. Collecting Personal Information"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Section Content (HTML supported)</label>
                      <textarea
                        className="input-modern min-h-[150px]"
                        value={sec.content}
                        onChange={(e) => updateSection(index, 'content', e.target.value)}
                        placeholder="Detailed clause content (HTML paragraphs <p>, lists <ul> <li>, bold <b> tags, etc.)"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end">
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-premium flex items-center gap-2"
            >
              <FaSave className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default LegalPagesCMS;
